import { Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { BetaBadge } from '~/components/shared/BetaBadge'
import { useAutoUpdateCheck } from '~/hooks/useAutoUpdateCheck'
import { useChannelSwitcher } from '~/hooks/useChannelSwitcher'
import { useSharedSettingsStyle } from '~/pages/Settings/componentStyles/SharedSettings.style'
import { SubheadingSection } from '~/pages/Settings/Shared'
import { isElectron } from '~/utils/common'

import { FeatureFlag } from '@mangovoice/constants'
import { useFeatureFlag } from '@mangovoice/core'

import { ChannelSwitcher } from './ChannelSwitcher'
import { ReleaseNotifications } from './ReleaseNotifications'

export const UpdateSettings = () => {
  const { t } = useTranslation()
  const sharedStyles = useSharedSettingsStyle()
  // Auto-run a check when opened via the release email's update deep link
  // (`mango://settings/updates?check`) — no-op otherwise (CUS-877).
  useAutoUpdateCheck()
  const releaseNotificationsFlag = useFeatureFlag(
    FeatureFlag.ReleaseNotifications,
  )
  const showReleaseNotifications =
    releaseNotificationsFlag.isReady && releaseNotificationsFlag.isEnabled

  // Channel-conditional blurb: most users are on Production and don't know
  // that's our internal name for it, so say nothing about the channel there
  // (and on dev — mirrors ChannelBadge, which hides for prod/dev). On any other
  // channel, name it via its displayName (e.g. Beta, Nightly). Falls back to the
  // generic line while the channel resolves or if it can't be named (CUS-590).
  const { currentChannel, availableChannels } = useChannelSwitcher()
  const channelInfo = availableChannels.find((c) => c.name === currentChannel)
  const nameChannelInDescription =
    !!currentChannel &&
    currentChannel !== 'prod' &&
    currentChannel !== 'dev' &&
    !!channelInfo?.displayName
  // Only name the channel when we have a real displayName for it — never expose
  // the raw lowercase key (e.g. an internalOnly channel filtered out of a
  // non-internal user's availableChannels), which would read as "…on the beta
  // channel". Otherwise fall back to the generic line.
  const releaseDescription = nameChannelInDescription
    ? t('updateSettings.releaseNotifications.descriptionWithChannel', {
        channel: channelInfo.displayName,
      })
    : t('updateSettings.releaseNotifications.description')

  if (!isElectron) return null

  return (
    <Flex css={sharedStyles.wrapper}>
      <SubheadingSection id="release-channel">
        <Text textStyle={'Body2_Bold'}>
          {t('updateSettings.channelSwitcher.title')}
        </Text>
        <Flex marginBottom={'1rem'}>
          <Text textStyle={'Body2'}>
            {t('updateSettings.channelSwitcher.description')}
          </Text>
        </Flex>
        <ChannelSwitcher />
      </SubheadingSection>
      {showReleaseNotifications && (
        <SubheadingSection id="release-notifications">
          <Flex align="center" gap="0.5rem">
            <Text textStyle={'Body2_Bold'}>
              {t('updateSettings.releaseNotifications.title')}
            </Text>
            <BetaBadge />
          </Flex>
          <Flex marginBottom={'1rem'}>
            <Text textStyle={'Body2'}>{releaseDescription}</Text>
          </Flex>
          <ReleaseNotifications />
        </SubheadingSection>
      )}
    </Flex>
  )
}
